/**
 * useWalletDisplay - Custom hook for wallet address display and resolution
 * Handles Avvy name resolution with proper cleanup
 */

import { useState, useEffect, useMemo } from 'react';
import { ethers } from 'ethers';
import { resolveAvvyName } from '../utils/resolveName';
import { logInfo } from '../utils/log';

export const useWalletDisplay = (walletAddress, networkUrl) => {
  const [displayAddress, setDisplayAddress] = useState('');

  // Memoized provider with fallback
  const provider = useMemo(() => {
    if (networkUrl) {
      return new ethers.providers.JsonRpcProvider(networkUrl);
    }
    // Fallback provider for development
    return new ethers.providers.JsonRpcProvider('https://api.avax-test.network/ext/bc/C/rpc');
  }, [networkUrl]);

  useEffect(() => {
    let isMounted = true;

    const resolveAddress = async () => {
      if (!walletAddress || !provider) {
        setDisplayAddress('');
        return;
      }

      try {
        const avvyName = await resolveAvvyName(walletAddress, provider);
        
        if (isMounted) {
          const display = avvyName || `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`;
          setDisplayAddress(display);
          logInfo('Wallet address resolved:', display);
        }
      } catch (error) {
        if (isMounted) {
          // Fallback to truncated address
          const fallback = `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`;
          setDisplayAddress(fallback);
          logInfo('Avvy resolution failed, using fallback:', fallback);
        }
      }
    };

    resolveAddress();

    return () => {
      isMounted = false;
    };
  }, [walletAddress, provider]);

  return displayAddress;
};
